import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";

// Extended color logic to support ReportView labels while keeping the snippet's defaults
const scoreToColor = (label) => {
  if (label === "skills" || label === "skillsMatch") return "#1a73e8"; // Blue
  if (label === "diversity") return "#ff6d00"; // Orange
  if (label === "conversion") return "#9c27b0"; // Purple
  if (label === "passRate") return "#1e8e3e"; // Green
  return "#34a853"; // Default Green (behaviors)
};

const fadedColor = (label) => {
  if (label === "skills" || label === "skillsMatch") return "#e3f2fd";
  if (label === "diversity") return "#fce8e6";
  if (label === "conversion") return "#f3e5f5";
  if (label === "passRate") return "#e6f4ea";
  return "#e6f4ea";
};

const AnimatedMetricDisplay = ({ score = 0, label, title, size = 'medium' }) => {
  const [animate, setAnimate] = useState(false);

  // Trigger animation on mount
  useEffect(() => {
    const timeout = setTimeout(() => setAnimate(true), 300);
    return () => clearTimeout(timeout);
  }, []);

  // Configuration from your snippet
  const circleCount = 9;
  const filledCount = Math.round(score * circleCount);
  const angles = [60, 75, 95, 120, 150, 185, 222, 265, 315];
  const scalePattern = [0.7, 1.10, 1.45, 1.75, 1.95, 2.15, 2.35, 2.95, 3.25];
  const radius = 80;
  const baseSize = 20;
  const containerSize = 240; // Base size from snippet

  // Scale factor logic for reusability (ReportView uses 'small'/'medium')
  let scaleFactor = 1;
  if (size === 'small') scaleFactor = 0.6;
  if (size === 'medium') scaleFactor = 0.8;
  if (size === 'large') scaleFactor = 1.0;

  return (
    <Box 
      sx={{ 
        position: "relative", 
        width: containerSize * scaleFactor, 
        height: containerSize * scaleFactor, 
        margin: 'auto',
        // Scale the internal content to fit the new container size
        '& > div': { transformOrigin: 'top left', transform: `scale(${scaleFactor})` }
      }}
    >
      {/* Inner Container that holds the original 240x240 layout */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: containerSize, height: containerSize }}>
        {angles.map((angle, i) => {
          const rad = (angle * Math.PI) / 180;
          const x = (containerSize / 2) + radius * Math.cos(rad);
          const y = (containerSize / 2) + radius * Math.sin(rad);
          const circleSize = baseSize * scalePattern[i];
          
          return (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: y,
                left: x,
                width: circleSize,
                height: circleSize,
                borderRadius: "50%",
                backgroundColor: i < (animate ? filledCount : 0) ? scoreToColor(label) : fadedColor(label),
                transform: "translate(-50%, -50%)",
                transition: "background-color 0.6s ease",
                transitionDelay: `${i * 0.1}s`
              }}
            />
          );
        })}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            width: '100%'
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: scoreToColor(label) }}>
            {Math.round(score * 100)}%
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {title || (label === "skills" ? "Skill Score" : "Behavior Score")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AnimatedMetricDisplay;